import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';
import starIcon from '../../assets/dashboard_assets/star.png';
import PageTitle from './components/PageTitle';

const TYPE_FILTERS = [
    { key: '', label: 'Total' },
    { key: 'listing', label: 'Listings' },
    { key: 'listing_score', label: 'Listing Score' },
    { key: 'deal_tag', label: 'Deal Tag' },
    { key: 'title_update', label: 'Title Update' },
    { key: 'catalogue_fix', label: 'Catalogue Fixes' },
    { key: 'refund', label: 'Refunds' },
    { key: 'topup', label: 'Credits added' },
];

const TYPE_BADGE = {
    listing: 'bg-[#D0E7FF]/40 text-[#2766AA]',
    listing_score: 'bg-[#FFF4E5] text-[#B36B00]',
    deal_tag: 'bg-[#F0EBFF] text-[#5B3FB8]',
    title_update: 'bg-[#E5F3FF] text-[#1E5F8C]',
    catalogue_fix: 'bg-[#E8F7ED] text-[#2E9E5C]',
    // Money coming back in — green, same family as a positive amount.
    refund: 'bg-[#E8F7ED] text-[#2E9E5C]',
    topup: 'bg-[#E8F7ED] text-[#2E9E5C]',
};

const formatDate = (iso) => {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso;
        return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
            + ' · ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    } catch {
        return iso;
    }
};

const UsageHistoryPage = () => {
    const authCtx = useContext(AuthContext);
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [perPage] = useState(20);
    const [total, setTotal] = useState(0);
    const [type, setType] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Lifetime spend per type + overall, from the server. Computed there with
    // aggregations over the whole history, so it does not shrink as rows are
    // paginated or capped.
    const [totals, setTotals] = useState({ by_type: {}, total_spent: 0, total_added: 0 });

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

    useEffect(() => {
        if (!authCtx.token) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        const qs = new URLSearchParams({ page: String(page), per_page: String(perPage) });
        if (type) qs.set('type', type);
        (async () => {
            try {
                const res = await fetch(`${API_URL}/auth/usage-history?${qs.toString()}`, {
                    headers: { Authorization: `Bearer ${authCtx.token}` },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (cancelled) return;
                setItems(data.items || []);
                setTotal(data.total || 0);
                if (data.totals) setTotals(data.totals);
            } catch (e) {
                if (!cancelled) setError(e.message || 'Failed to load history');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [authCtx.token, API_URL, page, perPage, type]);

    const lastPage = Math.max(1, Math.ceil(total / perPage));
    // Spend = charges minus refunds. Top-ups are counted separately, never
    // netted against spend — a 200-credit grant on the page must not make it
    // look like 200 credits of usage were undone.
    const spentPage = items
        .filter((it) => it.type !== 'topup')
        .reduce((s, it) => s + (it.direction === 'credit' ? -(it.credits || 0) : (it.credits || 0)), 0);
    const addedPage = items
        .filter((it) => it.type === 'topup')
        .reduce((s, it) => s + (it.credits || 0), 0);

    // Lifetime number behind each tab. "All" carries the grand total; "Credits
    // added" is money in, not spend, so it reads from its own figure. Refunds
    // have none of their own — they are already netted off the type they
    // repaid, so showing a separate refund total would double-count.
    const activeFilter = TYPE_FILTERS.find((f) => f.key === type) || TYPE_FILTERS[0];

    const tabTotal = (key) => {
        if (key === '') return totals.total_spent || 0;
        if (key === 'topup') return totals.total_added || 0;
        if (key === 'refund') return 0;
        return (totals.by_type || {})[key] || 0;
    };
    // Rows from before the credit ledger existed carry an estimated amount.
    const hasEstimated = items.some((it) => it.estimated);

    // A bulk run is charged in full up front and refunded for whatever failed,
    // so charge and refund are two rows sharing one group_key. Pair them up to
    // show what the run actually cost — on its own, "−356 / +120" says little.
    const netByGroup = items.reduce((acc, it) => {
        if (!it.group_key) return acc;
        const g = acc[it.group_key] || { charged: 0, refunded: 0 };
        if (it.direction === 'credit') g.refunded += it.credits || 0;
        else g.charged += it.credits || 0;
        acc[it.group_key] = g;
        return acc;
    }, {});

    return (
        <div className="w-full max-w-[1550px] mx-auto bg-white shadow-[0_0_134px_14px_rgba(120,179,243,0.07)] rounded-2xl overflow-hidden p-6 pb-16 mb-16 origin-top" style={{ transform: 'scale(0.9)' }}>
            {/* Back link */}
            <button
                onClick={() => navigate('/dashboard/plans')}
                className="text-[#2766AA] text-sm font-semibold hover:underline bg-transparent border-none p-0 mb-2 cursor-pointer"
            >
                ← Back to Buy Credits
            </button>

            <PageTitle title="Usage history" badge="Every credit charged, refunded and added to your account." />

            {/* Filter chips + page credits summary */}
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div className="flex flex-wrap gap-2">
                    {TYPE_FILTERS.map(f => (
                        <button
                            key={f.key || 'all'}
                            onClick={() => { setType(f.key); setPage(1); }}
                            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                                type === f.key
                                    ? 'bg-gradient-to-r from-[#2766AA] to-[#0752B0] text-white'
                                    : 'bg-white text-[#505050] hover:bg-[#F8FBFF] border border-[#D0E7FF]'
                            }`}
                        >
                            {f.label}
                            {(() => {
                                const n = tabTotal(f.key);
                                if (!n) return null;
                                return (
                                    <span className={`ml-1.5 font-bold ${type === f.key ? 'text-white/80' : 'text-[#898989]'}`}>
                                        {n.toLocaleString()}
                                    </span>
                                );
                            })()}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white/80 rounded-full outline outline-1 outline-[#D0E7FF]">
                    <img src={starIcon} alt="" className="w-4 h-4" />
                    {/* Follows the open tab: on "Listings" this reads the
                        Listings total, not the grand total, so the headline
                        number always matches the rows below it. */}
                    <span className="text-[#737373] text-xs font-semibold uppercase tracking-wide">
                        {type === 'topup' ? 'Total added:' : `${activeFilter.label} spent:`}
                    </span>
                    <span className={`text-sm font-bold ${type === 'topup' ? 'text-[#2E9E5C]' : 'text-[#3A3A3A]'}`}>
                        {tabTotal(type).toLocaleString()} credits
                    </span>
                    {type === '' && totals.total_added > 0 && (
                        <span className="text-[#2E9E5C] text-sm font-bold">+{totals.total_added.toLocaleString()} added</span>
                    )}
                    <span className="text-[#A3A3A3] text-xs font-medium border-l border-[#D0E7FF] pl-2">
                        {spentPage.toLocaleString()} on this page
                        {addedPage > 0 && ` · +${addedPage.toLocaleString()}`}
                    </span>
                </div>
            </div>

            {/* List card */}
            <div className="w-full bg-white/80 shadow-[0_0_134px_14px_rgba(120,179,243,0.06)] rounded-3xl outline outline-1 outline-[#D0E7FF] px-5 py-5">
                {loading ? (
                    <div className="py-16 text-center text-[#898989] text-sm font-medium">Loading…</div>
                ) : error ? (
                    <div className="py-16 text-center text-red-500 text-sm font-medium">{error}</div>
                ) : items.length === 0 ? (
                    <div className="py-16 text-center text-[#898989] text-sm font-medium">No activity yet.</div>
                ) : (
                    <>
                        {/* Table header */}
                        <div className="grid grid-cols-[160px_1fr_180px_120px] gap-4 px-4 py-2 text-[#737373] text-xs font-semibold uppercase tracking-wide">
                            <span>Type</span>
                            <span>Detail</span>
                            <span>When</span>
                            <span className="text-right">Credits</span>
                        </div>
                        <div className="w-full h-px bg-[#D0E7FF] mb-2" />

                        {/* Rows */}
                        <div className="flex flex-col gap-2">
                            {items.map((it, i) => (
                                <div
                                    key={`${it.resource_id || i}-${i}`}
                                    className="grid grid-cols-[160px_1fr_180px_120px] gap-4 px-4 py-3 bg-white rounded-2xl outline outline-1 outline-[#E8F0FE] items-center hover:outline-[#D0E7FF] hover:bg-[#F8FBFF] transition-colors"
                                >
                                    <span
                                        className={`px-3 py-1 rounded-full text-xs font-semibold w-fit ${TYPE_BADGE[it.type] || 'bg-[#F8FBFF] text-[#505050]'}`}
                                    >
                                        {it.label}
                                    </span>
                                    <span className="text-[#505050] text-sm font-medium truncate" title={it.detail || ''}>
                                        {it.detail || '—'}
                                        {(() => {
                                            const g = it.group_key && netByGroup[it.group_key];
                                            // Only worth saying once both halves are on this
                                            // page — otherwise the net is misleading.
                                            if (!g || !g.charged || !g.refunded) return null;
                                            return (
                                                <span className="ml-2 text-[#898989] text-xs font-semibold">
                                                    · net {(g.charged - g.refunded).toLocaleString()} of {g.charged.toLocaleString()}
                                                </span>
                                            );
                                        })()}
                                    </span>
                                    <span className="text-[#737373] text-xs font-medium">
                                        {formatDate(it.timestamp)}
                                    </span>
                                    <div className="flex items-center justify-end gap-1.5">
                                        <img src={starIcon} alt="" className="w-3.5 h-3.5" />
                                        <span
                                            className={`text-sm font-bold ${it.direction === 'credit' ? 'text-[#2E9E5C]' : 'text-[#3A3A3A]'}`}
                                            title={it.estimated ? 'Estimated at the published rate — this predates per-request credit tracking.' : undefined}
                                        >
                                            {it.direction === 'credit' ? '+' : '−'}{(it.credits || 0).toLocaleString()}
                                            {it.estimated && <span className="text-[#A3A3A3] font-semibold">*</span>}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {!loading && hasEstimated && (
                    <p className="mt-4 px-4 text-[#898989] text-xs font-medium">
                        * Estimated at the published rate. These actions predate per-request
                        credit tracking, so any refund on them isn't reflected.
                    </p>
                )}

                {/* Pagination */}
                {!loading && total > perPage && (
                    <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#D0E7FF]">
                        <span className="text-[#737373] text-sm font-medium">
                            Page <span className="text-[#3A3A3A] font-semibold">{page}</span> of <span className="text-[#3A3A3A] font-semibold">{lastPage}</span> · {total.toLocaleString()} total
                        </span>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                className="px-5 py-2 rounded-lg outline outline-[1.5px] outline-offset-[-1.5px] outline-[#2766AA] text-[#2766AA] text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#2766AA] hover:text-white transition-colors bg-transparent"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setPage(p => Math.min(lastPage, p + 1))}
                                disabled={page >= lastPage}
                                className="px-5 py-2 rounded-lg bg-gradient-to-r from-[#2766AA] to-[#0752B0] text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UsageHistoryPage;
